import React, {useState, useEffect} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL;

const OFFRES_POMPAGE = [
    {cv: 3, kwc: 4, prix: 11000},
    {cv: 5, kwc: 8, prix: 19000},
    {cv: 7.5, kwc: 10, prix: 24000},
    {cv: 10, kwc: 13, prix: 34000},
    {cv: 15, kwc: 18, prix: 40000},
    {cv: 20, kwc: 25, prix: 60000},
    {cv: 25, kwc: 30, prix: 75000}
];

const NewQuoteForm = ({onSuccess, onCancel}) => {
    const [step, setStep] = useState(1);
    const [selectedType, setSelectedType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [calculResult, setCalculResult] = useState(null);
    const [offrePompageSelectionnee, setOffrePompageSelectionnee] = useState(null);
    const [formData, setFormData] = useState({
        consommationMensuelle: '',
        periodeConsommation: 'mensuelle',
        modePaiement: 'credit',
        puissanceCV: '',
        surfaceHectares: '',
        debitSouhaite: '',
        hauteurRefoulement: '',
        typeCulture: '',
        budgetPompage: '',
        avisPhotovoltaique: '',
        perspective: ''
    });

    const calculateResults = (consommation, periode, mode) => {
        if (!consommation || consommation <= 0) {
            setCalculResult(null);
            return;
        }

        let consommationAnnuelle;
        switch (periode) {
            case 'mensuelle': consommationAnnuelle = parseFloat(consommation) * 12; break;
            case 'trimestrielle': consommationAnnuelle = parseFloat(consommation) * 4; break;
            case 'annuelle': consommationAnnuelle = parseFloat(consommation); break;
            default: consommationAnnuelle = parseFloat(consommation) * 12;
        }

        const puissanceNecessaire = consommationAnnuelle / 1600;
        const valeursCommerciales = [1, 2, 3, 5, 7, 10, 15, 20, 25, 30, 40, 50];
        let puissanceCommerciale = valeursCommerciales[0];
        for (let i = 0; i < valeursCommerciales.length; i++) {
            if (Math.abs(valeursCommerciales[i] - puissanceNecessaire) < Math.abs(puissanceCommerciale - puissanceNecessaire)) {
                puissanceCommerciale = valeursCommerciales[i];
            }
        }

        if (puissanceCommerciale < 2) puissanceCommerciale = 2;
        if (puissanceNecessaire > 2 && puissanceNecessaire <= 3) puissanceCommerciale = 3;
        else if (puissanceNecessaire > 3 && puissanceNecessaire <= 4) puissanceCommerciale = 4;

        const isIndustrielle = puissanceCommerciale > 10;

        let typeFormulaire, prixFormulaire, typeContrat, autofinancement = 0, coutTotal = 0;

        if (mode === 'comptant') {
            typeFormulaire = 'Non applicable';
            prixFormulaire = 0;
            typeContrat = 'Hors programme Prosol';
        } else {
            if (puissanceCommerciale <= 2) {
                typeFormulaire = 'Formulaire 7500 DT';
                prixFormulaire = 7500;
                typeContrat = 'Crédit Prosol Élec';
            } else if (puissanceCommerciale <= 4) {
                typeFormulaire = 'Formulaire 10000 DT';
                prixFormulaire = 10000;
                typeContrat = 'Crédit Prosol Élec';
            } else {
                typeFormulaire = 'Formulaire 10000 DT';
                prixFormulaire = 10000;
                typeContrat = 'Crédit + Autofinancement';
                const autofinancementMap = {5: 6000, 7: 9000, 8: 13000, 9: 17000, 10: 22000};
                autofinancement = autofinancementMap[puissanceCommerciale] || 0;
                coutTotal = prixFormulaire + autofinancement;
            }
        }

        setCalculResult({
            consommationAnnuelle: Math.round(consommationAnnuelle),
            puissanceNecessaire: Math.round(puissanceNecessaire * 100) / 100,
            puissanceCommerciale,
            typeFormulaire,
            prixFormulaire,
            typeContrat,
            autofinancement,
            coutTotal,
            isIndustrielle
        });
    };

    const handleChange = (e) => {
        const {name, value} = e.target;
        const newFormData = {...formData, [name]: value};
        setFormData(newFormData);

        if ((selectedType === 'photovoltaique' || selectedType === 'both') &&
            ['consommationMensuelle', 'periodeConsommation', 'modePaiement'].includes(name)) {
            calculateResults(
                name === 'consommationMensuelle' ? value : newFormData.consommationMensuelle,
                name === 'periodeConsommation' ? value : newFormData.periodeConsommation,
                name === 'modePaiement' ? value : newFormData.modePaiement
            );
        }

        if (name === 'puissanceCV' && value) {
            const cv = parseFloat(value);
            let offre = OFFRES_POMPAGE.find(o => o.cv === cv);
            if (!offre && cv > 0) {
                offre = OFFRES_POMPAGE.find(o => o.cv > cv) || OFFRES_POMPAGE[OFFRES_POMPAGE.length - 1];
            }
            setOffrePompageSelectionnee(offre || null);
        }
    };

    const handleTypeSelection = (type) => {
        setSelectedType(type);
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const requestData = {
                avisPhotovoltaique: formData.avisPhotovoltaique,
                perspective: formData.perspective
            };

            if (selectedType === 'photovoltaique' || selectedType === 'both') {
                requestData.consommationMensuelle = parseFloat(formData.consommationMensuelle);
                requestData.periodeConsommation = formData.periodeConsommation;
                requestData.modePaiement = formData.modePaiement;
                requestData.pompage = false;
            }

            if (selectedType === 'pompage' || selectedType === 'both') {
                requestData.pompage = true;
                requestData.puissanceCV = parseFloat(formData.puissanceCV);
                if (offrePompageSelectionnee) {
                    requestData.puissanceKWC = offrePompageSelectionnee.kwc;
                    requestData.prixPompage = offrePompageSelectionnee.prix;
                }
            }

            await axios.post(`${API_URL}/demandes-devis`, requestData, {
                headers: {Authorization: `Bearer ${token}`}
            });

            Swal.fire({
                icon: 'success',
                title: 'Demande envoyée',
                text: 'Votre demande de devis entreprise avec prédiction a été enregistrée.',
                confirmButtonColor: '#1e3a5f'
            });

            onSuccess();
        } catch (error) {
            console.error('Erreur envoi:', error);
            Swal.fire('Erreur', 'Impossible d\'envoyer la demande.', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (step === 1) {
        return (
            <div className="quote-type-selection">
                <div className="selection-header">
                    <h3>Nouvelle Demande B2B</h3>
                    <p>Sélectionnez le type d'installation pour votre entreprise</p>
                </div>
                <div className="type-options">
                    <div className="type-opt" onClick={() => handleTypeSelection('photovoltaique')}>
                        <div className="opt-icon">⚡</div>
                        <h4>Photovoltaïque</h4>
                        <p>Autoconsommation industrielle</p>
                    </div>
                    <div className="type-opt" onClick={() => handleTypeSelection('pompage')}>
                        <div className="opt-icon">💧</div>
                        <h4>Pompage Solaire</h4>
                        <p>Irrigation haute performance</p>
                    </div>
                    <div className="type-opt" onClick={() => handleTypeSelection('both')}>
                        <div className="opt-icon">🌟</div>
                        <h4>Solution Mixte</h4>
                        <p>Optimisation totale</p>
                    </div>
                </div>
                <button className="cancel-btn-simple" onClick={onCancel}>Annuler</button>
            </div>
        );
    }

    return (
        <div className="quote-form-container">
            <div className="form-header-simple">
                <button onClick={() => setStep(1)} className="back-link">← Retour</button>
                <h3>Configuration & Prédiction</h3>
            </div>

            <div className="form-split">
                <form onSubmit={handleSubmit} className="premium-form-half">
                    {(selectedType === 'photovoltaique' || selectedType === 'both') && (
                        <div className="form-section-simple">
                            <h5>Données Électriques</h5>
                            <div className="form-row-simple">
                                <div className="field-group">
                                    <label>Période</label>
                                    <select name="periodeConsommation" value={formData.periodeConsommation} onChange={handleChange}>
                                        <option value="mensuelle">Mensuelle</option>
                                        <option value="trimestrielle">Trimestrielle</option>
                                        <option value="annuelle">Annuelle</option>
                                    </select>
                                </div>
                                <div className="field-group">
                                    <label>Consommation (kWh)</label>
                                    <input type="number" name="consommationMensuelle" value={formData.consommationMensuelle} onChange={handleChange} required />
                                </div>
                            </div>
                            <div className="field-group" style={{marginTop: '10px'}}>
                                <label>Mode de paiement</label>
                                <select name="modePaiement" value={formData.modePaiement} onChange={handleChange}>
                                    <option value="credit">Crédit Prosol</option>
                                    <option value="comptant">Paiement Comptant</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {(selectedType === 'pompage' || selectedType === 'both') && (
                        <div className="form-section-simple">
                            <h5>Données Pompage</h5>
                            <div className="form-row-simple">
                                <div className="field-group">
                                    <label>Puissance Pompe (CV)</label>
                                    <input type="number" name="puissanceCV" value={formData.puissanceCV} onChange={handleChange} required placeholder="Ex: 5" />
                                </div>
                                <div className="field-group">
                                    <label>Surface (Ha)</label>
                                    <input type="number" name="surfaceHectares" value={formData.surfaceHectares} onChange={handleChange} placeholder="Ex: 2" />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="form-section-simple">
                        <h5>Description du Projet</h5>
                        <div className="field-group">
                            <label>Objectifs</label>
                            <textarea name="perspective" value={formData.perspective} onChange={handleChange} required placeholder="Vos objectifs..." rows="2"></textarea>
                        </div>
                        <div className="field-group" style={{marginTop: '10px'}}>
                            <label>Avis technique</label>
                            <textarea name="avisPhotovoltaique" value={formData.avisPhotovoltaique} onChange={handleChange} required placeholder="Votre avis..." rows="2"></textarea>
                        </div>
                    </div>

                    <div className="form-actions-simple">
                        <button type="submit" className="submit-btn-simple" disabled={loading}>
                            {loading ? 'Envoi...' : 'Confirmer la demande'}
                        </button>
                    </div>
                </form>

                <div className="prediction-panel">
                    <h4>📊 Prédiction Experte</h4>
                    {!calculResult && !offrePompageSelectionnee && (
                        <div className="empty-prediction">
                            Saisissez vos données pour obtenir une estimation immédiate.
                        </div>
                    )}

                    {calculResult && (selectedType === 'photovoltaique' || selectedType === 'both') && (
                        <div className="prediction-block">
                            {calculResult.isIndustrielle ? (
                                <div className="industrial-alert">
                                    <strong>Installation Industrielle</strong>
                                    <p>Votre puissance dépasse 10 kWc. Un expert prendra contact pour une étude technique sur mesure.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="pred-item">
                                        <span className="p-label">Puissance recommandée:</span>
                                        <span className="p-val">{calculResult.puissanceCommerciale} kWc</span>
                                    </div>
                                    <div className="pred-item">
                                        <span className="p-label">Type Contrat:</span>
                                        <span className="p-val">{calculResult.typeContrat}</span>
                                    </div>
                                    {calculResult.prixFormulaire > 0 && (
                                        <div className="pred-item highlight-blue">
                                            <span className="p-label">💰 Prix Formulaire:</span>
                                            <span className="p-val">{calculResult.prixFormulaire} DT</span>
                                        </div>
                                    )}
                                    {calculResult.autofinancement > 0 && (
                                        <div className="pred-item highlight-orange">
                                            <span className="p-label">🏦 Autofinancement:</span>
                                            <span className="p-val">{calculResult.autofinancement} DT</span>
                                            <small style={{fontSize: '0.7rem', color: '#9a3412', marginTop: '5px'}}>
                                                Requis pour les puissances &gt; 4kWc
                                            </small>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {offrePompageSelectionnee && (selectedType === 'pompage' || selectedType === 'both') && (
                        <div className="prediction-block" style={{marginTop: '15px'}}>
                            <div className="pred-item">
                                <span className="p-label">Système recommandé:</span>
                                <span className="p-val">{offrePompageSelectionnee.kwc} kWc ({offrePompageSelectionnee.cv} CV)</span>
                            </div>
                            <div className="pred-item highlight-green">
                                <span className="p-label">💵 Prix Estimatif:</span>
                                <span className="p-val">{offrePompageSelectionnee.prix} DT</span>
                            </div>
                        </div>
                    )}

                    {(calculResult || offrePompageSelectionnee) && (
                        <div className="prediction-note">
                            * Estimations basées sur les tarifs Prosol 2024. Le prix final dépend de l'étude sur site par nos ingénieurs.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewQuoteForm;
